// Hybrid Supabase auth storage adapter.
//
// Why this exists: on WEB, an OWNER or EMPLOYEE session must end when the browser
// tab is CLOSED, but survive a reload. That is exactly sessionStorage semantics
// ("survives reload, dies on close"). CLIENT and ADMIN sessions stay persistent
// (localStorage). We decide per-write by reading the role out of the session JWT.
//
// On NATIVE (iOS / Android) the rule is the opposite and does not depend on the
// role: every session goes to the system vault (Keychain / Keystore) via
// `nativeAuthStore.ts`, so closing the app no longer logs anyone out. See the
// 2026-08-28 trace in docs/features/auth-inscription/.
//
// Important constraints we self-protect against:
//  - The Supabase SDK calls a custom `storage` adapter RAW (no try/catch wrapper,
//    unlike its default localStorage path), so every storage access here is wrapped
//    and never throws out of the adapter.
//  - Safari private mode / disabled storage can throw on access. On any throw we
//    fall back to an in-memory Map per store so auth still works (just non-persistent).
//  - The PKCE code-verifier is stored as a JSON-quoted string (not a session object),
//    so it decodes to a null role and stays in localStorage. Keeping the verifier in
//    localStorage means an OAuth / reset link that lands in another tab still works.
//  - On NATIVE, this adapter delegates ALL of its safety guarantees to
//    `nativeAuthStore.ts` (the vault, the in-memory fallback, the web-copy
//    migration) instead of the local `safeGet`/`safeSet` mechanism below, which
//    exists for the web path only.

import type { SupportedStorage } from '@supabase/supabase-js';
import { nativeGetItem, nativeSetItem, nativeRemoveItem } from './nativeAuthStore';
// `isNative()` est enveloppé dans son propre module (partagé avec `nativeAuthStore.ts`) :
// si le pont Capacitor manque, mieux vaut retomber sur le chemin web que perdre la
// session en jetant depuis un adaptateur que le SDK Supabase appelle SANS try/catch.
import { isNative } from '@/lib/isNativePlatform';
import { ANNOUNCEMENT_OPEN_KEY, resetAnnouncementOpenState } from '@/lib/announcementOpenState';

// Roles whose session must die when the tab / app is closed.
const SESSION_SCOPED_ROLES = new Set(['owner', 'employee']);

// Per-store in-memory fallbacks, used only when the real Web Storage throws
// (e.g. Safari private mode, storage disabled). Module-level so they persist for
// the lifetime of the page.
const localFallback = new Map<string, string>();
const sessionFallback = new Map<string, string>();

type StoreKind = 'local' | 'session';

// Resolve the underlying Web Storage object defensively. Returns null if storage
// is unavailable (no window, or access itself throws).
function getStore(kind: StoreKind): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function getFallback(kind: StoreKind): Map<string, string> {
  return kind === 'local' ? localFallback : sessionFallback;
}

// Safe get: tries the real store, then the in-memory fallback. Never throws.
function safeGet(kind: StoreKind, key: string): string | null {
  const store = getStore(kind);
  if (store) {
    try {
      const value = store.getItem(key);
      if (value !== null) return value;
    } catch {
      // fall through to the in-memory fallback
    }
  }
  const fb = getFallback(kind);
  return fb.has(key) ? fb.get(key)! : null;
}

// Safe set: writes to the real store; if that throws, keeps the value in memory so
// it stays retrievable for this session. Never throws.
function safeSet(kind: StoreKind, key: string, value: string): void {
  const store = getStore(kind);
  if (store) {
    try {
      store.setItem(key, value);
      // Clear any stale in-memory copy now that the real store accepted the write.
      getFallback(kind).delete(key);
      return;
    } catch {
      // fall through to the in-memory fallback
    }
  }
  getFallback(kind).set(key, value);
}

// Safe remove: removes from both the real store and the in-memory fallback. Never throws.
function safeRemove(kind: StoreKind, key: string): void {
  const store = getStore(kind);
  if (store) {
    try {
      store.removeItem(key);
    } catch {
      // ignore; we still clear the fallback below
    }
  }
  getFallback(kind).delete(key);
}

// Décode la charge utile du JWT d'une valeur de session stockée. `null` sur
// TOUT problème (pas un objet session, JWT malformé, vérifieur PKCE, etc.).
function decodeJwtPayloadFromValue(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return null;
    if (typeof parsed.access_token !== 'string') return null;

    const segment = parsed.access_token.split('.')[1];
    if (!segment) return null;

    // base64url -> base64, then decode as UTF-8 safe JSON.
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(escape(atob(padded)));
    const payload = JSON.parse(json);
    return typeof payload === 'object' && payload !== null ? payload : null;
  } catch {
    return null;
  }
}

// Decode the Supabase role out of a stored session value.
// Returns null on ANY problem. A null role means "route to localStorage" (safe default).
export function decodeRoleFromValue(value: string): string | null {
  const payload = decodeJwtPayloadFromValue(value) as { app_metadata?: { role?: string } } | null;
  return payload?.app_metadata?.role ?? null;
}

// L'identifiant de la personne (`sub` du JWT), `null` si illisible ou absent.
export function decodeSubFromValue(value: string): string | null {
  const sub = decodeJwtPayloadFromValue(value)?.sub;
  return typeof sub === 'string' && sub !== '' ? sub : null;
}

function isSessionScoped(value: string): boolean {
  const role = decodeRoleFromValue(value);
  return role !== null && SESSION_SCOPED_ROLES.has(role);
}

// La clé de SESSION d'auth-js (`sb-<ref>-auth-token`). Le vérifieur PKCE
// (`…-code-verifier`) et `…-user` ne correspondent pas : ils gardent leur routage.
//
// ⚠️ Dupliquée dans `nativeAuthStore.ts` (fonction `clearNativeAuthStore`), même
// valeur, raison d'être différente (déconnexion web synchrone ici, déconnexion
// natif là-bas). Si l'une change sans l'autre, la déconnexion web et la
// déconnexion native divergent silencieusement. Pas d'export partagé : ce serait
// un couplage de plus pour peu de gain.
const AUTH_TOKEN_RE = /^sb-.*-auth-token$/;

// Marque d'onglet du personnel (spec du 2026-10-02,
// `.superpowers/specs/2026-10-02-session-client-preservee-multi-onglets-design.md`).
//
// POURQUOI : la session vit sous UNE seule clé pour tous les onglets. Celle du
// personnel est dans le sessionStorage de son onglet, celle d'un client dans le
// localStorage PARTAGÉ. Sans marque, un onglet du personnel effaçait la session
// du client d'un autre onglet en se connectant ou en se déconnectant, et, une
// fois déconnecté, il RELISAIT cette session client (la garde anti-écho
// refusait alors sa propre déconnexion). La marque dit « cet onglet appartient
// au personnel » : il ne lit plus le localStorage pour la clé de session, et
// n'efface que son propre emplacement. Elle meurt avec l'onglet. Décision de
// Julien du 2026-10-02 : l'onglet du personnel reste anonyme après sa sortie.
export const STAFF_TAB_MARK_KEY = 'flowcut_staff_tab';

function isStaffTab(): boolean {
  return safeGet('session', STAFF_TAB_MARK_KEY) !== null;
}

function markStaffTab(): void {
  safeSet('session', STAFF_TAB_MARK_KEY, '1');
}

// Même personne : deux `sub` lisibles et égaux. Un `sub` illisible compte comme
// « pas la même personne » (sens sûr : on garde la session de l'autre onglet).
function sameSub(a: string, b: string): boolean {
  const subA = decodeSubFromValue(a);
  return subA !== null && subA === decodeSubFromValue(b);
}

// Sur natif, le jeton part dans le coffre système (Keychain / Keystore) au lieu du
// stockage web : c'est ce qui le fait survivre à la fermeture de l'app. Le web garde
// la sémantique du 2026-06-24 (owner et employé meurent avec l'onglet).

// Le vérifieur PKCE reste dans le stockage web même sur natif : valeur de courte
// durée, sans intérêt une fois consommée, et au cœur du flux Google OAuth, qui a
// un long historique de bogues ici. On le reconnaît à sa clé, pas à sa valeur.
function isPkceVerifierKey(key: string): boolean {
  return key.endsWith('-code-verifier');
}

// ⚠️ Ne PAS renommer en `useVault`. Ce n'est pas un hook React, c'est un simple
// prédicat, mais `react-hooks/rules-of-hooks` ne juge que sur le préfixe `use` :
// sous l'ancien nom, la règle criait sur les TROIS appels ci-dessous et faisait
// rougir la CI. Le préfixe `should` dit la même chose sans mentir au linteur.
function shouldUseVault(key: string): boolean {
  return isNative() && !isPkceVerifierKey(key);
}

// The adapter handed to supabase-js as `auth.storage`. Self-protecting; synchronous
// on web, but returns a Promise on native (the vault calls are all async).
export const hybridAuthStorage: SupportedStorage = {
  getItem(key: string): string | null | Promise<string | null> {
    if (shouldUseVault(key)) return nativeGetItem(key);

    // sessionStorage has priority; fall back to localStorage so a client token or a
    // pre-scoping write is still found.
    const fromSession = safeGet('session', key);
    if (fromSession !== null) return fromSession;
    // Onglet du personnel : la session partagée appartient à un client d'un
    // autre onglet, jamais à celui-ci.
    if (AUTH_TOKEN_RE.test(key) && isStaffTab()) return null;
    return safeGet('local', key);
  },

  setItem(key: string, value: string): void | Promise<void> {
    if (shouldUseVault(key)) return nativeSetItem(key, value);

    if (isSessionScoped(value)) {
      // Owner/employee: keep only in sessionStorage so closing the tab/app logs out.
      safeSet('session', key, value);
      if (AUTH_TOKEN_RE.test(key)) markStaffTab();
      // N'effacer la copie partagée que si elle est à nous : une vieille copie du
      // personnel (avant le cloisonnement du 2026-06-24) ou la même personne.
      // La session client d'un autre onglet reste intacte.
      const shared = safeGet('local', key);
      if (shared !== null && (isSessionScoped(shared) || sameSub(shared, value))) {
        safeRemove('local', key);
      }
    } else {
      // Everyone else (and the PKCE verifier): persist in localStorage.
      safeSet('local', key, value);
      safeRemove('session', key);
      // Une session client rangée ici : l'onglet n'est plus celui du personnel.
      if (AUTH_TOKEN_RE.test(key)) safeRemove('session', STAFF_TAB_MARK_KEY);
    }
  },

  removeItem(key: string): void | Promise<void> {
    if (shouldUseVault(key)) return nativeRemoveItem(key);

    // Onglet du personnel, marqué ou dont la session est dans son propre
    // emplacement (onglet rechargé après le déploiement, pas encore marqué) :
    // n'efface que cet emplacement et garde la marque, l'onglet reste anonyme.
    if (AUTH_TOKEN_RE.test(key) && (isStaffTab() || safeGet('session', key) !== null)) {
      safeRemove('session', key);
      markStaffTab();
      return;
    }
    // Client qui se déconnecte dans son propre onglet, ou clé annexe (vérifieur
    // PKCE, `-user`) : le rôle est inconnu, on vide les deux emplacements.
    safeRemove('session', key);
    safeRemove('local', key);
  },
};

// Synchronous owner-session clear, used by the home-nav logout: it guarantees no
// auto-resume even if the page navigates away before any async work completes.
// Removes every Supabase auth-token key from sessionStorage, staff (owner/employee)
// auth-token keys from localStorage (a client session of another tab is kept), plus
// the single-session token keys from BOTH stores. Leaves the PKCE -code-verifier
// keys intact (they do not match the $-anchored auth-token pattern). Never throws.
export function clearOwnerSessionSync(): void {
  // Le marqueur d'annonce n'est plus un littéral orphelin : il appartient à
  // `@/lib/announcementOpenState`, un module feuille sans import React ni
  // Supabase, donc importable ici sans coupler cet adaptateur bas niveau à un
  // composant. On garde la clé dans la liste ci-dessous ET on appelle le reset
  // du module : la liste balaie les DEUX stockages plus le repli en mémoire,
  // ce que le reset ne fait pas, et le reset remet la variable de portée module
  // à zéro, ce que la liste ne fait pas. Les deux sont nécessaires.
  //
  // Sans ça, sur un appareil partagé (tablette d'accueil de salon, un seul
  // onglet ouvert toute la journée), seule la première personne connectée voit
  // jamais une annonce.
  const SINGLE_SESSION_KEYS = [
    'flowcut_session_token',
    'flowcut_session_token_at',
    ANNOUNCEMENT_OPEN_KEY,
  ];

  resetAnnouncementOpenState();

  const kinds: StoreKind[] = ['session', 'local'];
  for (const kind of kinds) {
    // Collect matching auth-token keys from the real store before mutating it.
    const keysToRemove: string[] = [];
    const store = getStore(kind);
    if (store) {
      try {
        for (let i = 0; i < store.length; i++) {
          const key = store.key(i);
          if (key && AUTH_TOKEN_RE.test(key)) keysToRemove.push(key);
        }
      } catch {
        // ignore; we still clear the in-memory fallback and the single-session keys
      }
    }
    // Also scan the in-memory fallback (covers the storage-disabled path).
    for (const key of getFallback(kind).keys()) {
      if (AUTH_TOKEN_RE.test(key)) keysToRemove.push(key);
    }

    // Un onglet du personnel ouvert avant le déploiement n'a pas encore de marque.
    // Vider sa copie sans le marquer ferait retomber le signOut asynchrone qui suit
    // sur le localStorage, donc sur la session d'un client d'un autre onglet.
    // Pas de marque sans copie : un refus de rôle client vit dans le localStorage
    // et doit y être effacé par le removeItem de son propre onglet.
    if (kind === 'session' && keysToRemove.length > 0) markStaffTab();

    for (const key of keysToRemove) {
      // Le localStorage est PARTAGÉ : n'y effacer que les sessions du personnel.
      // La session d'un client connecté dans un autre onglet n'est pas la nôtre
      // (spec du 2026-10-02). Le sessionStorage, lui, est à cet onglet seul.
      if (kind === 'local' && !isSessionScoped(safeGet('local', key) ?? '')) continue;
      safeRemove(kind, key);
    }
    for (const key of SINGLE_SESSION_KEYS) safeRemove(kind, key);
  }
}
